import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Layout } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { Content, Header } from 'antd/es/layout/layout';
import TopNav from './TopNav';
import useSetTitle from './GlobalSetTitleHook';

interface Lab {
  id: number;
  name: string;
  simple_name: string | null;
  lab_code: string;
  lab_company: string;
  remark: string | null;
  created_at: string;
  updated_at: string;
}

const LabManager: React.FC = () => {
  useSetTitle();
  const [labs, setLabs] = useState<Lab[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLab, setEditingLab] = useState<Lab | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchLabs();
  }, []);

  const fetchLabs = async () => {
    try {
      const response = await api.get('/api/lab/');
      setLabs(response.data);
    } catch (error) {
      message.error('获取实验室列表失败');
    }
  };

  const handleAddOrEdit = async (values: any) => {
    try {
      if (editingLab) {
        await api.put(`/api/lab/${editingLab.id}/`, values);
        message.success('更新成功');
      } else {
        await api.post('/api/lab/', values);
        message.success('添加成功');
      }
      setModalVisible(false);
      form.resetFields();
      fetchLabs();
    } catch (error) {
      message.error('保存失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/lab/${id}/`);
      message.success('删除成功');
      fetchLabs();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: '简称', dataIndex: 'simple_name', key: 'simple_name' },
    { title: '编码', dataIndex: 'lab_code', key: 'lab_code' },
    { title: '公司', dataIndex: 'lab_company', key: 'lab_company' },
    { title: '备注', dataIndex: 'remark', key: 'remark' },
    {
      title: '操作',
      key: 'action',
      render: (record: Lab) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingLab(record);
              form.setFieldsValue(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此实验室吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="是"
            cancelText="否"
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Layout className="layout">
      <Header style={{ background: '#f0f2f5', padding: 0 }}>
        <TopNav />
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <h2>实验室管理</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingLab(null);
              form.resetFields();
              setModalVisible(true);
            }}
            style={{ marginBottom: '20px' }}
          >
            添加实验室
          </Button>
          <Table
            columns={columns}
            dataSource={labs}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
          <Modal
            title={editingLab ? '编辑实验室' : '添加实验室'}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            onOk={() => form.submit()}
          >
            <Form form={form} onFinish={handleAddOrEdit} layout="vertical">
              <Form.Item
                label="名称"
                name="name"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="简称" name="simple_name">
                <Input />
              </Form.Item>
              <Form.Item
                label="编码"
                name="lab_code"
                rules={[{ required: true, message: '请输入编码' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="公司" name="lab_company">
                <Input />
              </Form.Item>
              <Form.Item label="备注" name="remark">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default LabManager;
